import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import DocumentHistoryDrawer from '@/components/feature/documents/DocumentHistoryDrawer';
import DocumentVariablesPanel from '@/components/feature/documents/DocumentVariablesPanel';
import SignatureRequestButton from '@/components/feature/documents/SignatureRequestButton';
import {
  getGeneratedDocumentSourceLabel,
  getGeneratedDocumentVersionLabel,
} from '@/lib/generatedDocumentPresentation';
import {
  downloadDocumentExportArtifact,
  type DocumentExportBranding,
} from '@/lib/documentExport';
import {
  buildDocumentExportBranding,
} from '@/lib/documentBranding';
import {
  docxDocumentExporter,
} from '@/lib/exporters/docxExporter';
import {
  pdfDocumentExporter,
} from '@/lib/exporters/pdfExporter';
import {
  buildVariableDefinitions,
} from '@/lib/documentTemplateVariables';
import {
  buildHistoricalDocumentVariableDefinitions,
  buildResolvedDocumentVariables,
  getMissingRequiredDocumentVariables,
} from '@/lib/documentVariableValues';
import { Drawer, EmptyState, LoadingState, SearchInput, useToast } from '@/components/base';
import { useAppContext } from '@/contexts/AppContext';
import { useCases, type Case } from '@/hooks/useCases';
import {
  useDocumentTemplates,
  type DocumentTemplate,
  type DocumentTemplateType,
} from '@/hooks/useDocumentTemplates';
import {
  useGeneratedDocuments,
  type LoadGeneratedDocumentsFilters,
  type GeneratedDocument,
} from '@/hooks/useGeneratedDocuments';
import { useLegalProducts, type LegalProduct } from '@/hooks/useLegalProducts';
import { useOfficeSettings, type OfficeSettingsFormState } from '@/hooks/useOfficeSettings';
import { useDocumentBranding } from '@/hooks/useDocumentBranding';
import { useLeads, type Lead } from '@/hooks/useLeads';
import type { Client } from '@/types/client';

const documentTypeLabels: Record<DocumentTemplateType, string> = {
  contract: 'Contrato',
  power_of_attorney: 'Procuração',
  declaration: 'Declaração',
  proposal: 'Proposta',
  petition: 'Petição',
  defense: 'Defesa',
  appeal: 'Recurso',
  notice: 'Notificação',
  other: 'Outro',
};

const documentTypeOptions = (Object.keys(documentTypeLabels) as DocumentTemplateType[]).map((value) => ({
  value,
  label: documentTypeLabels[value],
}));

const documentStatusLabels: Record<string, string> = {
  draft: 'Rascunho',
  reviewed: 'Revisado',
  finalized: 'Finalizado',
  archived: 'Arquivado',
};

const documentStatusClasses: Record<string, string> = {
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
  reviewed: 'bg-blue-50 text-blue-700 border-blue-200',
  finalized: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived: 'bg-gray-100 text-gray-500 border-gray-200',
};

const documentStatusFilterLabels: Record<string, string> = {
  active: 'Ativos',
  draft: 'Rascunhos',
  reviewed: 'Revisados',
  finalized: 'Finalizados',
  archived: 'Arquivados',
};

type DocumentStatusFilter = keyof typeof documentStatusFilterLabels;

interface GeneratedDocumentFormState {
  templateId: string;
  clientId: string;
  caseId: string;
  leadId: string;
  legalProductId: string;
  title: string;
  documentType: DocumentTemplateType;
  status: string;
  content: string;
  renderedContent: string;
  notes: string;
}

const emptyFormState = (): GeneratedDocumentFormState => ({
  templateId: '',
  clientId: '',
  caseId: '',
  leadId: '',
  legalProductId: '',
  title: '',
  documentType: 'contract',
  status: 'draft',
  content: '',
  renderedContent: '',
  notes: '',
});

const toNullableText = (value: string) => {
  const text = value.trim();
  return text === '' ? null : text;
};

const formatDate = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value.includes('T') ? value : `${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('pt-BR');
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '—';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const slugifyFileName = (title: string) => {
  const slug = title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug || 'documento-gerado';
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const downloadTextFile = (filename: string, content: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const getDocumentExportContent = (document: GeneratedDocument) => document.renderedContent?.trim() || document.content || '';

const exportDocumentAsTxt = (document: GeneratedDocument) => {
  const filename = `${slugifyFileName(document.title)}.txt`;
  downloadTextFile(filename, getDocumentExportContent(document), 'text/plain;charset=utf-8');
};

const exportDocumentAsHtml = (document: GeneratedDocument) => {
  const filename = `${slugifyFileName(document.title)}.html`;
  const content = escapeHtml(getDocumentExportContent(document)).replace(/\r?\n/g, '<br>');
  const html = `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(document.title)}</title>
</head>
<body>
  <div style="font-family: Arial, Helvetica, sans-serif; line-height: 1.6; white-space: pre-wrap;">${content}</div>
</body>
</html>`;
  downloadTextFile(filename, html, 'text/html;charset=utf-8');
};

const exportDocumentAsDocx = async (
  document: GeneratedDocument,
  branding: DocumentExportBranding
) => {
  const artifact =
    await docxDocumentExporter.export(
      {
        title:
          document.title,

        content:
          getDocumentExportContent(
            document
          ),

        creator:
          'BM Legal OS',

        subject:
          'Documento jurídico',

        branding,
      }
    );

  downloadDocumentExportArtifact(
    artifact
  );
};

const exportDocumentAsPdf = async (
  document: GeneratedDocument,
  branding: DocumentExportBranding
) => {
  const artifact =
    await pdfDocumentExporter.export(
      {
        title:
          document.title,

        content:
          getDocumentExportContent(
            document
          ),

        creator:
          'BM Legal OS',

        subject:
          'Documento jurídico',

        branding,
      }
    );

  downloadDocumentExportArtifact(
    artifact
  );
};

const joinNonEmpty = (...parts: Array<string | null | undefined>) =>
  parts
    .map((part) => String(part ?? '').trim())
    .filter((part) => part.length > 0)
    .join(', ');

const buildClientAddress = (client: Client | null | undefined) =>
  joinNonEmpty(
    joinNonEmpty(client?.street, client?.number),
    client?.complement,
    client?.neighborhood,
    joinNonEmpty(client?.city, client?.state),
    client?.zipCode
  );

const renderTemplateContent = (content: string, variables: Record<string, string>) =>
  content.replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (_match, token: string) => variables[token] ?? '');

const toDisplayValue = (value: string | null | undefined) => value?.trim() ?? '';

const buildOfficeAddress = (officeSettings: OfficeSettingsFormState | null) => {
  if (!officeSettings) {
    return '';
  }

  const parts = [
    officeSettings.addressLine,
    officeSettings.addressNumber,
    officeSettings.addressComplement,
    officeSettings.district,
    [officeSettings.city, officeSettings.state].filter((part) => part.trim()).join(' / '),
    officeSettings.zipCode,
    officeSettings.country,
  ]
    .map((part) => part.trim())
    .filter(Boolean);

  return parts.join(', ');
};

const buildVariablesSnapshot = ({
  client,
  caseItem,
  lead,
  product,
  officeSettings,
}: {
  client: Client | null;
  caseItem: Case | null;
  lead: Lead | null;
  product: LegalProduct | null;
  officeSettings: OfficeSettingsFormState | null;
}) => ({
  clientName: client?.name ?? '',
  clientCpfCnpj: client?.cpfCnpj ?? '',
  clientRg: client?.rg ?? '',
  clientNationality: client?.nationality ?? '',
  clientCivilStatus: client?.civilStatus ?? '',
  clientProfession: client?.profession ?? '',
  clientPhone: client?.phone ?? '',
  clientEmail: client?.email ?? '',
  clientAddress: buildClientAddress(client),
  clientCity: client?.city ?? '',
  clientState: client?.state ?? '',
  caseTitle: caseItem?.title ?? '',
  caseNumber: caseItem?.caseNumber ?? '',
  caseCourt: caseItem?.court ?? '',
  caseLegalArea: caseItem?.legalArea ?? '',
  caseOpposingParty: caseItem?.opposingParty ?? '',
  caseValue: caseItem?.caseValue === null || caseItem?.caseValue === undefined ? '' : formatCurrency(caseItem.caseValue),
  leadName: lead?.name ?? '',
  leadPhone: lead?.phone ?? '',
  leadEmail: lead?.email ?? '',
  legalProductName: product?.name ?? '',
  currentDate: new Date().toLocaleDateString('pt-BR'),
  officeName: toDisplayValue(officeSettings?.officeName),
  officeLegalName: toDisplayValue(officeSettings?.legalName),
  officeDocumentNumber: toDisplayValue(officeSettings?.documentNumber),
  officeWebsite: toDisplayValue(officeSettings?.website),
  officeEmail: toDisplayValue(officeSettings?.email),
  officePhone: toDisplayValue(officeSettings?.phone),
  officeWhatsapp: toDisplayValue(officeSettings?.whatsapp),
  officeInstagram: toDisplayValue(officeSettings?.instagram),
  officeAddress: buildOfficeAddress(officeSettings),
  officeAddressLine: toDisplayValue(officeSettings?.addressLine),
  officeAddressNumber: toDisplayValue(officeSettings?.addressNumber),
  officeAddressComplement: toDisplayValue(officeSettings?.addressComplement),
  officeDistrict: toDisplayValue(officeSettings?.district),
  officeCity: toDisplayValue(officeSettings?.city),
  officeState: toDisplayValue(officeSettings?.state),
  officeZipCode: toDisplayValue(officeSettings?.zipCode),
  officeCountry: toDisplayValue(officeSettings?.country),
  lawyerName: toDisplayValue(officeSettings?.lawyerName),
  lawyerDocument: toDisplayValue(officeSettings?.lawyerDocument),
  lawyerOab: toDisplayValue(officeSettings?.lawyerOab),
  lawyerOabState: toDisplayValue(officeSettings?.lawyerOabState),
  lawyerEmail: toDisplayValue(officeSettings?.lawyerEmail),
  lawyerPhone: toDisplayValue(officeSettings?.lawyerPhone),
  lawyerRole: toDisplayValue(officeSettings?.lawyerRole),
  bankName: toDisplayValue(officeSettings?.bankName),
  bankAgency: toDisplayValue(officeSettings?.bankAgency),
  bankAccount: toDisplayValue(officeSettings?.bankAccount),
  bankAccountType: toDisplayValue(officeSettings?.bankAccountType),
  pixKey: toDisplayValue(officeSettings?.pixKey),
  pixKeyType: toDisplayValue(officeSettings?.pixKeyType),
  paymentHolderName: toDisplayValue(officeSettings?.paymentHolderName),
  paymentHolderDocument: toDisplayValue(officeSettings?.paymentHolderDocument),
  defaultCity: toDisplayValue(officeSettings?.defaultCity),
  defaultState: toDisplayValue(officeSettings?.defaultState),
  defaultForum: toDisplayValue(officeSettings?.defaultForum),
  documentFooter: toDisplayValue(officeSettings?.documentFooter),
  signatureText: toDisplayValue(officeSettings?.signatureText),
  contractObservations: toDisplayValue(officeSettings?.contractObservations),
  powerOfAttorneyObservations: toDisplayValue(officeSettings?.powerOfAttorneyObservations),
  defaultCurrency: toDisplayValue(officeSettings?.defaultCurrency),
  defaultLanguage: toDisplayValue(officeSettings?.defaultLanguage),
  timezone: toDisplayValue(officeSettings?.timezone),
  dateFormat: toDisplayValue(officeSettings?.dateFormat),
});

const getDocumentTypeLabel = (value: DocumentTemplateType) => documentTypeLabels[value] ?? value;

const buildFormStateFromDocument = (document: GeneratedDocument): GeneratedDocumentFormState => ({
  templateId: document.templateId ?? '',
  clientId: document.clientId ?? '',
  caseId: document.caseId ?? '',
  leadId: document.leadId ?? '',
  legalProductId: document.legalProductId ?? '',
  title: document.title,
  documentType: (document.documentType as DocumentTemplateType) || 'other',
  status: document.status || 'draft',
  content: document.content,
  renderedContent: document.renderedContent ?? document.content,
  notes: document.notes ?? '',
});

const DocumentsPage: FC = () => {
  const { addToast } = useToast();
  const { clients, clientsLoading, clientsError } = useAppContext();
  const { cases, isLoadingCases } = useCases();
  const { leads, isLoadingLeads } = useLeads();
  const { legalProducts, isLoadingLegalProducts } = useLegalProducts();
  const { officeSettings } = useOfficeSettings();
  const { documentBranding } = useDocumentBranding();

  const documentExportBranding =
    useMemo(
      () =>
        buildDocumentExportBranding({
          branding:
            documentBranding,

          office:
            officeSettings,
        }),
      [
        documentBranding,
        officeSettings,
      ]
    );
  const { documentTemplates, isLoadingDocumentTemplates, documentTemplatesError } = useDocumentTemplates();
  const {
    generatedDocuments,
    isLoadingGeneratedDocuments,
    generatedDocumentsError,
    loadGeneratedDocuments,
    loadGeneratedDocumentHistory,
    createGeneratedDocument,
    updateGeneratedDocument,
    deleteGeneratedDocument,
  } = useGeneratedDocuments({ autoLoad: false });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DocumentStatusFilter>('active');
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [caseFilter, setCaseFilter] = useState<string>('all');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [editingDocument, setEditingDocument] = useState<GeneratedDocument | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState<GeneratedDocumentFormState>(emptyFormState);
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const [archivingDocumentId, setArchivingDocumentId] = useState<string | null>(null);
  const [updatingDocumentId, setUpdatingDocumentId] = useState<string | null>(null);
  const [updatingDocumentStatus, setUpdatingDocumentStatus] = useState<string | null>(null);
  const [duplicatingDocumentId, setDuplicatingDocumentId] = useState<string | null>(null);
  const [historyDocument, setHistoryDocument] = useState<GeneratedDocument | null>(null);
  const [manualVariableValues, setManualVariableValues] = useState<Record<string, string>>({});
  const [preservedVariableSnapshot, setPreservedVariableSnapshot] = useState<Record<string, string> | null>(null);
  const [preserveStoredRenderedContent, setPreserveStoredRenderedContent] = useState(false);

  const activeClients = useMemo(
    () => clients.filter((client) => client.status !== 'deleted').slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [clients]
  );
  const activeCases = useMemo(
    () => cases.filter((caseItem) => caseItem.status !== 'archived').slice().sort((a, b) => a.title.localeCompare(b.title, 'pt-BR')),
    [cases]
  );
  const activeLeads = useMemo(
    () => leads.filter((lead) => lead.status !== 'archived').slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [leads]
  );
  const activeLegalProducts = useMemo(
    () => legalProducts.filter((product) => product.isActive).slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [legalProducts]
  );
  const activeDocumentTemplates = useMemo(
    () => documentTemplates.filter((template) => template.isActive).slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [documentTemplates]
  );

  const buildLoadOptions = useCallback((filter: DocumentStatusFilter): LoadGeneratedDocumentsFilters => {
    if (filter !== 'archived') {
      return {};
    }

    return { includeArchived: true, status: 'archived' };
  }, []);

  const reloadDocuments = useCallback(async () => {
    await loadGeneratedDocuments(buildLoadOptions(statusFilter));
  }, [buildLoadOptions, loadGeneratedDocuments, statusFilter]);

  useEffect(() => {
    void reloadDocuments();
  }, [reloadDocuments]);

  const filteredDocuments = useMemo(
    () =>
      generatedDocuments.filter((document) => {
        if (search) {
          const haystack = [
            document.title,
            document.templateName ?? '',
            document.clientName ?? '',
            document.caseTitle ?? '',
            document.leadName ?? '',
            document.legalProductName ?? '',
            document.documentType,
            document.status,
            document.content ?? '',
            document.renderedContent ?? '',
            document.notes ?? '',
          ]
            .join(' ')
            .toLowerCase();

          if (!haystack.includes(search.toLowerCase())) {
            return false;
          }
        }

        if (statusFilter === 'active' && document.status === 'archived') {
          return false;
        }

        if (statusFilter !== 'active' && statusFilter !== 'archived' && document.status !== statusFilter) {
          return false;
        }

        if (statusFilter === 'archived' && document.status !== 'archived') {
          return false;
        }

        if (documentTypeFilter !== 'all' && document.documentType !== documentTypeFilter) {
          return false;
        }

        if (clientFilter !== 'all' && document.clientId !== clientFilter) {
          return false;
        }

        if (caseFilter !== 'all' && document.caseId !== caseFilter) {
          return false;
        }

        return true;
      }),
    [caseFilter, clientFilter, documentTypeFilter, generatedDocuments, search, statusFilter]
  );

  const selectedDocument = useMemo(
    () => generatedDocuments.find((document) => document.id === selectedDocumentId) ?? null,
    [generatedDocuments, selectedDocumentId]
  );

  const selectedTemplate = useMemo(
    () => activeDocumentTemplates.find((template) => template.id === form.templateId) ?? null,
    [activeDocumentTemplates, form.templateId]
  );
  const selectedClient = useMemo(
    () => activeClients.find((client) => client.id === form.clientId) ?? null,
    [activeClients, form.clientId]
  );
  const selectedCase = useMemo(
    () => activeCases.find((caseItem) => caseItem.id === form.caseId) ?? null,
    [activeCases, form.caseId]
  );
  const selectedLead = useMemo(
    () => activeLeads.find((lead) => lead.id === form.leadId) ?? null,
    [activeLeads, form.leadId]
  );
  const selectedProduct = useMemo(
    () => activeLegalProducts.find((product) => product.id === form.legalProductId) ?? null,
    [activeLegalProducts, form.legalProductId]
  );

  const automaticVariablesSnapshot = useMemo(
    () =>
      buildVariablesSnapshot({
        client: selectedClient,
        caseItem: selectedCase,
        lead: selectedLead,
        product: selectedProduct,
        officeSettings,
      }),
    [officeSettings, selectedCase, selectedClient, selectedLead, selectedProduct]
  );

  const documentVariableDefinitions = useMemo(() => {
    if (
      editingDocument &&
      preservedVariableSnapshot !== null
    ) {
      return buildHistoricalDocumentVariableDefinitions(
        editingDocument.templateVersionVariables,
        editingDocument.templateVersionVariablesSchema,
        preservedVariableSnapshot
      );
    }

    if (selectedTemplate) {
      return buildVariableDefinitions(
        selectedTemplate.variables,
        selectedTemplate.variablesSchema
      );
    }

    return buildVariableDefinitions(
      Object.keys(
        preservedVariableSnapshot ??
          editingDocument?.variablesSnapshot ??
          {}
      )
    );
  }, [
    editingDocument,
    preservedVariableSnapshot,
    selectedTemplate,
  ]);

  const baseVariablesSnapshot = useMemo(
    () =>
      preservedVariableSnapshot ??
      automaticVariablesSnapshot,
    [automaticVariablesSnapshot, preservedVariableSnapshot]
  );

  const resolvedVariablesSnapshot = useMemo(
    () =>
      buildResolvedDocumentVariables(
        baseVariablesSnapshot,
        manualVariableValues
      ),
    [baseVariablesSnapshot, manualVariableValues]
  );

  const missingRequiredVariables = useMemo(
    () =>
      getMissingRequiredDocumentVariables(
        documentVariableDefinitions,
        resolvedVariablesSnapshot
      ),
    [documentVariableDefinitions, resolvedVariablesSnapshot]
  );

  const generatedRenderedContent = useMemo(
    () =>
      renderTemplateContent(
        form.content,
        resolvedVariablesSnapshot
      ),
    [form.content, resolvedVariablesSnapshot]
  );

  useEffect(() => {
    if (!editorOpen || preserveStoredRenderedContent) {
      return;
    }

    setForm((prev) => {
      const nextRenderedContent = renderTemplateContent(
        prev.content,
        resolvedVariablesSnapshot
      );

      if (prev.renderedContent === nextRenderedContent) {
        return prev;
      }

      return {
        ...prev,
        renderedContent: nextRenderedContent,
      };
    });
  }, [
    editorOpen,
    preserveStoredRenderedContent,
    resolvedVariablesSnapshot,
  ]);

  const openCreateDrawer = () => {
    setEditingDocument(null);
    setForm(emptyFormState());
    setManualVariableValues({});
    setPreservedVariableSnapshot(null);
    setPreserveStoredRenderedContent(false);
    setEditorOpen(true);
  };

  const openEditDrawer = (document: GeneratedDocument) => {
    if (document.status === 'finalized') {
      const confirmed = window.confirm(
        'Este documento já foi finalizado. A edição não apagará a versão final anterior: o sistema criará uma nova revisão no histórico. Deseja continuar?'
      );

      if (!confirmed) {
        return;
      }
    }

    setEditingDocument(document);
    setForm(buildFormStateFromDocument(document));
    setManualVariableValues({});
    setPreservedVariableSnapshot({
      ...document.variablesSnapshot,
    });
    setPreserveStoredRenderedContent(true);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingDocument(null);
    setForm(emptyFormState());
    setManualVariableValues({});
    setPreservedVariableSnapshot(null);
    setPreserveStoredRenderedContent(false);
  };

  const handleTemplateSelection = (templateId: string) => {
    const template =
      activeDocumentTemplates.find(
        (item) => item.id === templateId
      ) ?? null;

    setManualVariableValues({});
    setPreservedVariableSnapshot(null);
    setPreserveStoredRenderedContent(false);

    if (!template) {
      setForm((prev) => ({
        ...prev,
        templateId,
      }));

      return;
    }

    const nextContent = template.content;
    const nextRenderedContent = renderTemplateContent(
      nextContent,
      automaticVariablesSnapshot
    );

    setForm((prev) => ({
      ...prev,
      templateId: template.id,
      documentType: template.documentType,
      legalProductId:
        prev.legalProductId ||
        template.legalProductId ||
        '',
      title:
        prev.title.trim()
          ? prev.title
          : template.name,
      content: nextContent,
      renderedContent: nextRenderedContent,
    }));
  };

  const updateManualVariable = (
    key: string,
    value: string
  ) => {
    setPreserveStoredRenderedContent(false);

    setManualVariableValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetManualVariable = (key: string) => {
    setPreserveStoredRenderedContent(false);

    setManualVariableValues((prev) => {
      const next = {
        ...prev,
      };

      delete next[key];

      return next;
    });
  };

  const refreshAutomaticVariableValues = () => {
    setPreservedVariableSnapshot(null);
    setPreserveStoredRenderedContent(false);
  };

  const saveDocument = async () => {
    if (isSavingDocument) {
      return;
    }

    if (!form.templateId.trim()) {
      addToast('Selecione um modelo de documento.', 'warning');
      return;
    }

    if (!form.title.trim()) {
      addToast('Preencha o título do documento.', 'warning');
      return;
    }

    if (!form.documentType.trim()) {
      addToast('Selecione o tipo do documento.', 'warning');
      return;
    }

    const content = form.content.trim();

    if (!content) {
      addToast('O documento não possui conteúdo para geração.', 'warning');
      return;
    }

    if (
      ['reviewed', 'finalized'].includes(form.status) &&
      missingRequiredVariables.length > 0
    ) {
      addToast(
        `Preencha as variáveis obrigatórias antes de ${form.status === 'finalized' ? 'finalizar' : 'marcar como revisado'}: ${missingRequiredVariables
          .map((item) => item.label)
          .join(', ')}.`,
        'warning'
      );

      return;
    }

    setIsSavingDocument(true);

    const payload = {
      templateId: form.templateId.trim(),
      clientId: toNullableText(form.clientId),
      caseId: toNullableText(form.caseId),
      leadId: toNullableText(form.leadId),
      legalProductId: toNullableText(form.legalProductId),
      title: form.title.trim(),
      documentType: form.documentType,
      status: form.status.trim() || 'draft',
      content,
      renderedContent: toNullableText(form.renderedContent) ?? generatedRenderedContent,
      variablesSnapshot: resolvedVariablesSnapshot,
      notes: toNullableText(form.notes),
      isActive: form.status.trim() !== 'archived',
    };

    try {
      const saved = editingDocument
        ? await updateGeneratedDocument(editingDocument.id, payload)
        : await createGeneratedDocument(payload);

      setSelectedDocumentId(saved.id);
      addToast(editingDocument ? 'Documento gerado atualizado com sucesso.' : 'Documento gerado com sucesso.', 'success');
      closeEditor();
      await reloadDocuments();
    } catch (error: unknown) {
      addToast(error instanceof Error ? error.message : 'Não foi possível salvar o documento gerado.', 'error');
    } finally {
      setIsSavingDocument(false);
    }
  };

  const archiveDocument = async (document: GeneratedDocument) => {
    if (isSavingDocument || archivingDocumentId) {
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja arquivar este documento gerado? Esta ação removerá o documento da listagem principal, mas manterá o registro no banco para auditoria.'
    );

    if (!confirmed) {
      return;
    }

    setArchivingDocumentId(document.id);

    try {
      await deleteGeneratedDocument(document.id);
      if (selectedDocumentId === document.id) {
        setSelectedDocumentId(null);
      }
      addToast('Documento arquivado com sucesso.', 'success');
      await reloadDocuments();
    } catch (error: unknown) {
      addToast(error instanceof Error ? error.message : 'Não foi possível arquivar o documento.', 'error');
    } finally {
      setArchivingDocumentId(null);
    }
  };

  const buildDocumentPayload = useCallback(
    (document: GeneratedDocument, overrides: Partial<{
      title: string;
      documentType: DocumentTemplateType;
      status: string;
      content: string;
      renderedContent: string | null;
      notes: string | null;
      isActive: boolean;
    }> = {}) => ({
      templateId: document.templateId ?? '',
      clientId: document.clientId,
      caseId: document.caseId,
      leadId: document.leadId,
      legalProductId: document.legalProductId,
      title: overrides.title ?? document.title,
      documentType: overrides.documentType ?? (document.documentType as DocumentTemplateType),
      status: overrides.status ?? document.status,
      content: overrides.content ?? document.content,
      renderedContent: overrides.renderedContent ?? document.renderedContent ?? document.content,
      variablesSnapshot: document.variablesSnapshot,
      notes: overrides.notes ?? document.notes,
      isActive: overrides.isActive ?? document.isActive,
    }),
    []
  );

  const updateDocumentStatus = async (document: GeneratedDocument, status: 'draft' | 'reviewed' | 'finalized') => {
    if (isSavingDocument || updatingDocumentId || archivingDocumentId || duplicatingDocumentId) {
      return;
    }

    if (document.status === status) {
      return;
    }

    if (status === 'reviewed' || status === 'finalized') {
      const definitions =
        buildHistoricalDocumentVariableDefinitions(
          document.templateVersionVariables,
          document.templateVersionVariablesSchema,
          document.variablesSnapshot
        );

      const missing =
        getMissingRequiredDocumentVariables(
          definitions,
          document.variablesSnapshot
        );

      if (missing.length > 0) {
        addToast(
          `Este documento possui variáveis obrigatórias sem preenchimento: ${missing
            .map((item) => item.label)
            .join(', ')}.`,
          'warning'
        );

        return;
      }
    }

    setUpdatingDocumentId(document.id);
    setUpdatingDocumentStatus(status);

    try {
      const nextStatus = status as string;
      const updated = await updateGeneratedDocument(
        document.id,
        buildDocumentPayload(document, { status: nextStatus, isActive: nextStatus !== 'archived' })
      );
      setSelectedDocumentId(updated.id);
      addToast(`Documento marcado como ${documentStatusLabels[status] ?? status}.`, 'success');
      await reloadDocuments();
    } catch (error: unknown) {
      addToast(error instanceof Error ? error.message : 'Não foi possível atualizar o status do documento.', 'error');
    } finally {
      setUpdatingDocumentId(null);
      setUpdatingDocumentStatus(null);
    }
  };

  const duplicateDocument = async (document: GeneratedDocument) => {
    if (isSavingDocument || updatingDocumentId || archivingDocumentId || duplicatingDocumentId) {
      return;
    }

    if (!document.templateId) {
      addToast('Não foi possível duplicar este documento porque o modelo vinculado não está disponível.', 'warning');
      return;
    }

    setDuplicatingDocumentId(document.id);

    try {
      const created = await createGeneratedDocument(
        buildDocumentPayload(document, {
          title: `Cópia de ${document.title}`,
          status: 'draft',
          isActive: true,
        })
      );

      setSelectedDocumentId(created.id);
      addToast('Documento duplicado com sucesso.', 'success');
      await reloadDocuments();
    } catch (error: unknown) {
      addToast(error instanceof Error ? error.message : 'Não foi possível duplicar o documento.', 'error');
    } finally {
      setDuplicatingDocumentId(null);
    }
  };

  const renderDocumentActions = (document: GeneratedDocument) => {
    const isDocumentBusy =
      isSavingDocument ||
      archivingDocumentId === document.id ||
      updatingDocumentId === document.id ||
      duplicatingDocumentId === document.id;

    const statusActions: Array<{ status: 'draft' | 'reviewed' | 'finalized'; label: string; icon: string }> = [
      { status: 'draft', label: 'Rascunho', icon: 'ri-restart-line' },
      { status: 'reviewed', label: 'Revisado', icon: 'ri-eye-line' },
      { status: 'finalized', label: 'Finalizado', icon: 'ri-check-line' },
    ];

    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setHistoryDocument(document);
          }}
          disabled={isDocumentBusy}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-[10px] font-medium text-foreground-600 hover:bg-violet-50 hover:text-violet-600 transition-colors cursor-pointer disabled:opacity-60"
          title="Histórico e revisões"
        >
          <i className="ri-history-line"></i>
          <span>Histórico</span>
        </button>

        {statusActions
          .filter((action) => action.status !== document.status)
          .map((action) => {
            const isUpdatingThisStatus = updatingDocumentId === document.id && updatingDocumentStatus === action.status;

            return (
              <button
                key={action.status}
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  void updateDocumentStatus(document, action.status);
                }}
                disabled={isDocumentBusy}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-[10px] font-medium text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer disabled:opacity-60"
                title={`Marcar como ${action.label.toLowerCase()}`}
              >
                {isUpdatingThisStatus ? <i className="ri-loader-4-line animate-spin"></i> : <i className={action.icon}></i>}
                <span>{action.label}</span>
              </button>
            );
          })}

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            void duplicateDocument(document);
          }}
          disabled={isDocumentBusy}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-[10px] font-medium text-foreground-600 hover:bg-cyan-50 hover:text-cyan-600 transition-colors cursor-pointer disabled:opacity-60"
          title="Duplicar"
        >
          {duplicatingDocumentId === document.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-file-copy-line"></i>}
          <span>Duplicar</span>
        </button>

        <SignatureRequestButton
          document={document}
          branding={documentExportBranding}
        />

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            exportDocumentAsTxt(document);
          }}
          disabled={isDocumentBusy}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-[10px] font-medium text-foreground-600 hover:bg-sky-50 hover:text-sky-600 transition-colors cursor-pointer disabled:opacity-60"
          title="Exportar TXT"
        >
          <i className="ri-file-text-line"></i>
          <span>TXT</span>
        </button>

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            exportDocumentAsHtml(document);
          }}
          disabled={isDocumentBusy}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-[10px] font-medium text-foreground-600 hover:bg-sky-50 hover:text-sky-600 transition-colors cursor-pointer disabled:opacity-60"
          title="Exportar HTML"
        >
          <i className="ri-html5-line"></i>
          <span>HTML</span>
        </button>

        <button
          type="button"
          onClick={async (event) => {
            event.stopPropagation();

            const content =
              getDocumentExportContent(
                document
              ).trim();

            if (!content) {
              addToast(
                'O documento não possui conteúdo para exportação.',
                'warning'
              );

              return;
            }

            try {
              await exportDocumentAsDocx(
                document,
                documentExportBranding
              );

              addToast(
                'Arquivo DOCX gerado com sucesso.',
                'success'
              );
            } catch (error) {
              console.error(
                '[documents] falha ao gerar DOCX',
                error
              );

              addToast(
                error instanceof Error
                  ? error.message
                  : 'Não foi possível gerar o arquivo DOCX.',
                'error'
              );
            }
          }}
          disabled={isDocumentBusy}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-[10px] font-medium text-foreground-600 hover:bg-violet-50 hover:text-violet-600 transition-colors cursor-pointer disabled:opacity-60"
          title="Exportar DOCX"
        >
          <i className="ri-file-word-line"></i>
          <span>DOCX</span>
        </button>

        <button
          type="button"
          onClick={async (event) => {
            event.stopPropagation();

            const content =
              getDocumentExportContent(
                document
              ).trim();

            if (!content) {
              addToast(
                'O documento não possui conteúdo para exportação.',
                'warning'
              );

              return;
            }

            try {
              await exportDocumentAsPdf(
                document,
                documentExportBranding
              );

              addToast(
                'Arquivo PDF gerado com sucesso.',
                'success'
              );
            } catch (error) {
              console.error(
                '[documents] falha ao gerar PDF',
                error
              );

              addToast(
                error instanceof Error
                  ? error.message
                  : 'Não foi possível gerar o arquivo PDF.',
                'error'
              );
            }
          }}
          disabled={isDocumentBusy}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-[10px] font-medium text-foreground-600 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer disabled:opacity-60"
          title="Exportar PDF"
        >
          <i className="ri-file-pdf-line"></i>
          <span>PDF</span>
        </button>

        {document.status !== 'archived' && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              void archiveDocument(document);
            }}
            disabled={isDocumentBusy}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-[10px] font-medium text-foreground-600 hover:bg-rose-50 hover:text-rose-600 transition-colors cursor-pointer disabled:opacity-60"
            title="Arquivar"
          >
            {archivingDocumentId === document.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-archive-line"></i>}
            <span>Arquivar</span>
          </button>
        )}
      </div>
    );
  };

  const selectedTemplateMissing =
    Boolean(form.templateId) && !activeDocumentTemplates.some((template) => template.id === form.templateId);
  const selectedClientMissing = Boolean(form.clientId) && !activeClients.some((client) => client.id === form.clientId);
  const selectedCaseMissing = Boolean(form.caseId) && !activeCases.some((caseItem) => caseItem.id === form.caseId);
  const selectedLeadMissing = Boolean(form.leadId) && !activeLeads.some((lead) => lead.id === form.leadId);
  const selectedProductMissing =
    Boolean(form.legalProductId) && !activeLegalProducts.some((product) => product.id === form.legalProductId);

  const caseOptions = form.clientId
    ? activeCases.filter((caseItem) => caseItem.clientId === form.clientId)
    : activeCases;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Documentos Gerados</h2>
            <p className="text-sm text-foreground-500 mt-0.5">
              {filteredDocuments.length} documento(s) · visão geral do escritório
            </p>
          </div>
          <button
            onClick={openCreateDrawer}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Novo Documento
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar documento..." className="sm:w-80" />

          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(documentStatusFilterLabels) as DocumentStatusFilter[]).map((filter) => (
              <button
                key={filter}
                onClick={() => setStatusFilter(filter)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  statusFilter === filter
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {documentStatusFilterLabels[filter]}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <select
              value={documentTypeFilter}
              onChange={(event) => setDocumentTypeFilter(event.target.value)}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
            >
              <option value="all">Todos os tipos</option>
              {documentTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={clientFilter}
              onChange={(event) => setClientFilter(event.target.value)}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
            >
              <option value="all">Todos os clientes</option>
              {activeClients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>

            <select
              value={caseFilter}
              onChange={(event) => setCaseFilter(event.target.value)}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
            >
              <option value="all">Todos os casos</option>
              {activeCases.map((caseItem) => (
                <option key={caseItem.id} value={caseItem.id}>
                  {caseItem.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {generatedDocumentsError && (
          <div className="flex items-center justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <i className="ri-alert-line"></i>
              <span>{generatedDocumentsError}</span>
            </div>
            <button
              onClick={() => void reloadDocuments()}
              className="text-xs font-medium text-amber-700 hover:text-amber-800 cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {documentTemplatesError && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <i className="ri-information-line"></i>
            <span>{documentTemplatesError}</span>
          </div>
        )}

        {isLoadingGeneratedDocuments ? (
          <LoadingState message="Carregando documentos gerados operacionais..." />
        ) : filteredDocuments.length === 0 ? (
          <EmptyState
            icon="ri-file-copy-line"
            title="Nenhum documento gerado encontrado"
            description="Crie o primeiro documento gerado para começar a registrar contratos, procurações e peças do escritório."
            action={{
              label: 'Novo Documento',
              onClick: openCreateDrawer,
              icon: 'ri-add-line',
            }}
          />
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.85fr)] gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredDocuments.map((document) => {
                const isSelected = document.id === selectedDocumentId;

                return (
                  <div
                    key={document.id}
                    onClick={() => setSelectedDocumentId(document.id)}
                    className={`bg-background-50 border rounded-xl p-4 cursor-pointer transition-all relative ${
                      isSelected
                        ? 'border-primary-300 ring-1 ring-primary-200'
                        : 'border-background-200 hover:border-primary-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 font-medium whitespace-nowrap">
                        {getDocumentTypeLabel(document.documentType as DocumentTemplateType)}
                      </span>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${
                          documentStatusClasses[document.status] ?? documentStatusClasses.draft
                        }`}
                      >
                        {documentStatusLabels[document.status] ?? document.status}
                      </span>
                    </div>

                    <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2">
                      {document.title}
                    </h4>
                    <p className="text-xs text-foreground-400 mt-1.5 line-clamp-2">
                      {document.templateName ?? 'Modelo não vinculado'}
                    </p>

                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[10px] font-semibold">
                        {getGeneratedDocumentVersionLabel(document.currentVersion)}
                      </span>

                      <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium">
                        {getGeneratedDocumentSourceLabel(document.source, Boolean(document.templateId))}
                      </span>

                      {document.templateVersionNumber && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium">
                          modelo v{document.templateVersionNumber}
                        </span>
                      )}
                    </div>

                    <div className="mt-3 space-y-1 text-xs text-foreground-500">
                      <p className="line-clamp-1">Cliente: {document.clientName ?? '—'}</p>
                      <p className="line-clamp-1">Caso: {document.caseTitle ?? '—'}</p>
                      <p className="line-clamp-1">Lead: {document.leadName ?? '—'}</p>
                      <p className="line-clamp-1">Produto: {document.legalProductName ?? '—'}</p>
                    </div>

                    <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-background-100">
                      <span className="text-xs text-foreground-400">Atualizado: {formatDate(document.updatedAt)}</span>
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openEditDrawer(document);
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-[10px] font-medium text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                          title="Editar"
                        >
                          <i className="ri-edit-line"></i>
                          <span>Editar</span>
                        </button>
                        {renderDocumentActions(document)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-background-50 border border-background-200 rounded-xl p-4">
              {selectedDocument ? (
                <div>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-[10px] px-2 py-0.5 inline-flex rounded-full bg-background-100 text-foreground-500 font-medium whitespace-nowrap">
                        {documentStatusLabels[selectedDocument.status] ?? selectedDocument.status}
                      </p>
                      <h3 className="text-base font-semibold text-foreground-900 mt-2">{selectedDocument.title}</h3>
                      <p className="text-sm text-foreground-500 mt-0.5">
                        {selectedDocument.templateName ?? 'Modelo não vinculado'}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => openEditDrawer(selectedDocument)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-[10px] font-medium text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-edit-line"></i>
                        <span>Editar</span>
                      </button>
                      {renderDocumentActions(selectedDocument)}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-foreground-400">Template</span>
                      <p className="text-foreground-700">{selectedDocument.templateName ?? '—'}</p>
                    </div>
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-foreground-400">Tipo</span>
                      <p className="text-foreground-700">
                        {documentTypeLabels[selectedDocument.documentType as DocumentTemplateType] ?? selectedDocument.documentType}
                      </p>
                    </div>
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-foreground-400">Cliente</span>
                      <p className="text-foreground-700">{selectedDocument.clientName ?? '—'}</p>
                    </div>
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-foreground-400">Caso</span>
                      <p className="text-foreground-700">{selectedDocument.caseTitle ?? '—'}</p>
                    </div>
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-foreground-400">Lead</span>
                      <p className="text-foreground-700">{selectedDocument.leadName ?? '—'}</p>
                    </div>
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-foreground-400">Produto jurídico</span>
                      <p className="text-foreground-700">{selectedDocument.legalProductName ?? '—'}</p>
                    </div>

                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-foreground-400">Versão atual</span>
                      <p className="text-foreground-700 font-semibold">
                        {getGeneratedDocumentVersionLabel(selectedDocument.currentVersion)}
                      </p>
                    </div>

                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-foreground-400">Versão do modelo usada</span>
                      <p className="text-foreground-700">
                        {selectedDocument.templateVersionNumber
                          ? `v${selectedDocument.templateVersionNumber}`
                          : '—'}
                      </p>
                    </div>

                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-foreground-400">Origem</span>
                      <p className="text-foreground-700">
                        {getGeneratedDocumentSourceLabel(
                          selectedDocument.source,
                          Boolean(selectedDocument.templateId)
                        )}
                      </p>
                    </div>

                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-foreground-400">Finalizado em</span>
                      <p className="text-foreground-700">
                        {selectedDocument.finalizedAt
                          ? formatDateTime(selectedDocument.finalizedAt)
                          : '—'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <span className="block text-[11px] uppercase tracking-wide text-foreground-400 mb-1">Conteúdo renderizado</span>
                    <div className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 whitespace-pre-line min-h-[140px]">
                      {selectedDocument.renderedContent || 'Sem conteúdo renderizado.'}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="block text-[11px] uppercase tracking-wide text-foreground-400">Observações</span>
                      <div className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 min-h-[80px] whitespace-pre-line">
                        {selectedDocument.notes || 'Sem observações.'}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-foreground-500">Criado em</span>
                        <span>{formatDateTime(selectedDocument.createdAt)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-foreground-500">Atualizado em</span>
                        <span>{formatDateTime(selectedDocument.updatedAt)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-foreground-500">Ativo</span>
                        <span>{selectedDocument.isActive ? 'Sim' : 'Não'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon="ri-file-search-line"
                  title="Selecione um documento"
                  description="Clique em um cartão da lista para ver o conteúdo renderizado e os metadados do documento."
                />
              )}
            </div>
          </div>
        )}

        <DocumentHistoryDrawer
          document={historyDocument}
          isOpen={Boolean(historyDocument)}
          onClose={() => setHistoryDocument(null)}
          loadHistory={loadGeneratedDocumentHistory}
        />

        <Drawer
          isOpen={editorOpen}
          onClose={closeEditor}
          title={editingDocument ? `Editar documento: ${editingDocument.title}` : 'Novo Documento Gerado'}
          width="w-[920px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeEditor}
                disabled={isSavingDocument}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                onClick={() => void saveDocument()}
                disabled={isSavingDocument}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {isSavingDocument ? 'Salvando...' : 'Salvar Documento'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            {editingDocument?.status === 'finalized' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <i className="ri-history-line mr-1.5"></i>
                Este documento já foi finalizado na versão{' '}
                <strong>
                  {getGeneratedDocumentVersionLabel(editingDocument.currentVersion)}
                </strong>.
                Ao salvar uma alteração, a versão anterior continuará preservada e uma nova revisão será criada.
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Modelo *</label>
              <select
                value={form.templateId}
                onChange={(event) =>
                  handleTemplateSelection(event.target.value)
                }
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="">Escolha um modelo...</option>
                {selectedTemplateMissing && form.templateId && (
                  <option value={form.templateId}>Modelo vinculado não encontrado</option>
                )}
                {activeDocumentTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Título *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Tipo *</label>
                <select
                  value={form.documentType}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, documentType: event.target.value as DocumentTemplateType }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {documentTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="draft">Rascunho</option>
                  <option value="reviewed">Revisado</option>
                  <option value="finalized">Finalizado</option>
                  <option value="archived">Arquivado</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Observações</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                  placeholder="Opcional"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Cliente</label>
                <select
                  value={form.clientId}
                  onChange={(event) => {
                    setPreservedVariableSnapshot(null);
                    setPreserveStoredRenderedContent(false);

                    setForm((prev) => ({
                      ...prev,
                      clientId: event.target.value,
                      caseId: event.target.value === prev.clientId ? prev.caseId : '',
                    }));
                  }}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem vínculo</option>
                  {selectedClientMissing && form.clientId && (
                    <option value={form.clientId}>Cliente vinculado não encontrado</option>
                  )}
                  {activeClients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Caso</label>
                <select
                  value={form.caseId}
                  onChange={(event) => {
                    setPreservedVariableSnapshot(null);
                    setPreserveStoredRenderedContent(false);
                    setForm((prev) => ({ ...prev, caseId: event.target.value }));
                  }}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem vínculo</option>
                  {selectedCaseMissing && form.caseId && (
                    <option value={form.caseId}>Caso vinculado não encontrado</option>
                  )}
                  {caseOptions.map((caseItem) => (
                    <option key={caseItem.id} value={caseItem.id}>
                      {caseItem.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Lead</label>
                <select
                  value={form.leadId}
                  onChange={(event) => {
                    setPreservedVariableSnapshot(null);
                    setPreserveStoredRenderedContent(false);
                    setForm((prev) => ({ ...prev, leadId: event.target.value }));
                  }}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem vínculo</option>
                  {selectedLeadMissing && form.leadId && (
                    <option value={form.leadId}>Lead vinculado não encontrado</option>
                  )}
                  {activeLeads.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Produto jurídico</label>
                <select
                  value={form.legalProductId}
                  onChange={(event) => {
                    setPreservedVariableSnapshot(null);
                    setPreserveStoredRenderedContent(false);
                    setForm((prev) => ({ ...prev, legalProductId: event.target.value }));
                  }}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem vínculo</option>
                  {selectedProductMissing && form.legalProductId && (
                    <option value={form.legalProductId}>Produto vinculado não encontrado</option>
                  )}
                  {activeLegalProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Conteúdo do modelo</label>
              <textarea
                value={selectedTemplate?.content ?? form.content}
                readOnly
                rows={8}
                className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 font-mono resize-none focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Conteúdo renderizado</label>
              <textarea
                value={form.renderedContent}
                onChange={(event) => {
                  setPreserveStoredRenderedContent(true);
                  setForm((prev) => ({ ...prev, renderedContent: event.target.value }));
                }}
                rows={12}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 font-mono resize-none focus:outline-none focus:border-primary-300"
                placeholder="O conteúdo renderizado será montado a partir dos dados selecionados..."
              />
              <p className="text-[11px] text-foreground-400 mt-1">
                As variáveis disponíveis são preenchidas com os controle operacional do cliente, caso, lead, produto e data atual.
              </p>
            </div>

            <DocumentVariablesPanel
              definitions={documentVariableDefinitions}
              automaticValues={automaticVariablesSnapshot}
              resolvedValues={resolvedVariablesSnapshot}
              manualValues={manualVariableValues}
              preservingSnapshot={preservedVariableSnapshot !== null}
              onChange={updateManualVariable}
              onReset={resetManualVariable}
              onRefreshAutomatic={refreshAutomaticVariableValues}
            />

            <div className="rounded-xl border border-background-200 bg-background-50 p-4">
              <label className="block text-xs font-medium text-foreground-600 mb-2">
                Ações de preenchimento
              </label>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPreserveStoredRenderedContent(false);

                    setForm((prev) => ({
                      ...prev,
                      renderedContent: generatedRenderedContent,
                    }));
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Regerar conteúdo
                </button>

                {Object.keys(manualVariableValues).length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setManualVariableValues({});
                      setPreserveStoredRenderedContent(false);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-eraser-line"></i>
                    Limpar substituições manuais
                  </button>
                )}
              </div>

              <p className="text-[11px] text-foreground-400 mt-2">
                As substituições feitas aqui pertencem somente a este documento e serão preservadas no snapshot da respectiva versão.
              </p>
            </div>
          </div>
        </Drawer>

        {(clientsLoading || clientsError || isLoadingCases || isLoadingLeads || isLoadingLegalProducts || isLoadingDocumentTemplates) && (
          <div className="flex flex-wrap gap-2 text-[11px] text-foreground-500">
            {clientsLoading && <span>Carregando clientes...</span>}
            {clientsError && <span>Clientes: {clientsError}</span>}
            {isLoadingCases && <span>Carregando casos...</span>}
            {isLoadingLeads && <span>Carregando leads...</span>}
            {isLoadingLegalProducts && <span>Carregando produtos jurídicos...</span>}
            {isLoadingDocumentTemplates && <span>Carregando modelos...</span>}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default DocumentsPage;
